import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { formatDate } from '../../../../utils/dateUtils';

// Predefined options for structured form fields with database codes
// These values match the method_id values in the rent_method table
const LEASE_METHODS = [
  { value: 'C', label: 'Non-Retail Commercial (Non-Government)' },
  { value: 'CG', label: 'Non-Retail Commercial (Government)' },
  { value: 'CH', label: 'Community housing (peppercorn)' },
  { value: 'PS', label: 'Public Space' },
  { value: 'R3', label: 'Retail - (Commercial) (Under 3 million)' },
  { value: 'R5', label: 'Retail - (Commercial) (Gross income over 5 mil)' },
  { value: 'RG', label: 'Residential (Government)' },
  { value: 'RN', label: 'Residential (Non-Government)' },
  { value: 'SD', label: 'Short-term Development Sublease' },
  { value: 'U', label: 'Undeveloped (large parcels)' }
];

// The 'value' is the 1-character code stored in the database (lease_type field is varchar(1))
// Note: We need to verify these values against the actual database table
const LEASE_TYPES = [
  { value: 'C', label: 'Commercial' },
  { value: 'R', label: 'Residential' },
  { value: 'I', label: 'Industrial' },
  { value: 'M', label: 'Mixed Use' },
  { value: 'O', label: 'Other' }
];

const REVIEW_INTERVALS = [
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Annually', label: 'Annually' },
  { value: 'When required', label: 'When Required' },
  { value: 'N/A', label: 'Not Applicable' }
];

const LEASE_DEALINGS = [
  { value: 'New', label: 'New Lease' },
  { value: 'Renewal', label: 'Renewal' },
  { value: 'Transfer', label: 'Transfer' },
  { value: 'Amendment', label: 'Amendment' }
];

interface EditSubLeaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sublease: {
    lot_id: string;
    holder_name: string | null;
    lease_start: string | null;
    lease_end: string | null;
    lot_perm_use: string | null;
    lease_rent_freq: string | null;
    community_name: string | null;
    lease_method?: string | null;
    lease_type?: string | null;
    lease_hmth_term?: string | null;
    lease_dealing?: string | null;
    lease_furth_term?: string | null;
    lease_deal_doc?: string | null;
    rental_amount?: number | null;
  };
}

export default function EditSubLeaseModal({ isOpen, onClose, onSuccess, sublease }: EditSubLeaseModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    endDate: sublease.lease_end || '',
    permittedUse: sublease.lot_perm_use || '',
    rentalAmount: sublease.rental_amount ? sublease.rental_amount.toString() : '',
    reviewInterval: sublease.lease_rent_freq || '',
    leaseMethod: sublease.lease_method || '',
    leaseType: sublease.lease_type || '',
    leaseDealing: sublease.lease_dealing || '',
    leaseFurthTerm: sublease.lease_furth_term || '',
    leaseDealDoc: sublease.lease_deal_doc || ''
  });

  // Update form data when sublease changes
  useEffect(() => {
    if (isOpen) {
      console.log('Initializing form with sublease data:', sublease);
      setFormData({
        endDate: sublease.lease_end || '',
        permittedUse: sublease.lot_perm_use || '',
        rentalAmount: sublease.rental_amount ? sublease.rental_amount.toString() : '',
        reviewInterval: sublease.lease_rent_freq || '',
        leaseMethod: sublease.lease_method || '',
        leaseType: sublease.lease_type || '',
        leaseDealing: sublease.lease_dealing || '',
        leaseFurthTerm: sublease.lease_furth_term || '',
        leaseDealDoc: sublease.lease_deal_doc || ''
      });
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen, sublease]);

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (formData.endDate && sublease.lease_start && new Date(sublease.lease_start) > new Date(formData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }

    // Remove the 30-character validation for permitted use

    if (formData.leaseFurthTerm && formData.leaseFurthTerm.length > 30) {
      errors.leaseFurthTerm = 'Further term cannot exceed 30 characters';
    }

    // Deal document has no explicit character limit now.

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // First, get the lease_id from lease_lots table
      // Modified to handle multiple rows by using order and limit instead of single()
      console.log('Fetching lease_id for lot:', sublease.lot_id);

      const { data: leaseLotsData, error: leaseLotsError } = await supabase
        .from('lease_lots')
        .select('leaselot_lease')
        .eq('leaselot_lot', sublease.lot_id)
        .order('leaselot_lease', { ascending: false }) // Get the most recent lease
        .limit(1);

      if (leaseLotsError) {
        console.error('Error fetching lease_lots:', leaseLotsError);
        throw new Error(`Failed to find lease for lot ${sublease.lot_id}: ${leaseLotsError.message}`);
      }

      if (!leaseLotsData || leaseLotsData.length === 0) {
        console.error('No lease found for lot:', sublease.lot_id);
        throw new Error(`No lease found for lot ${sublease.lot_id}`);
      }

      const leaseId = leaseLotsData[0].leaselot_lease;
      console.log('Found lease_id:', leaseId);

      // Get the selected lease method and type values
      // These values are already the correct database codes (e.g., 'C', 'CG', 'R', etc.)
      // as defined in the LEASE_METHODS and LEASE_TYPES constants

      // Log for debugging
      console.log('Selected lease method:', formData.leaseMethod);
      console.log('Selected lease type:', formData.leaseType);
      console.log('Rental amount:', formData.rentalAmount);

      // Use a transaction to ensure all updates succeed or fail together
      const { data, error: transactionError } = await supabase.rpc('update_sublease', {
        p_lease_id: leaseId,
        p_lot_id: sublease.lot_id,
        p_lease_end: formData.endDate || null,
        p_lease_rent_freq: formData.reviewInterval || null,
        p_lease_method: formData.leaseMethod || null, // Use the value directly
        p_lease_type: formData.leaseType || null, // Use the value directly
        p_lease_dealing: formData.leaseDealing || null,
        p_lease_furth_term: formData.leaseFurthTerm || null,
        p_lease_deal_doc: formData.leaseDealDoc || null,
        p_lot_perm_use: formData.permittedUse || null
      });

      if (transactionError) {
        console.error('Transaction error:', transactionError);
        throw new Error(`Failed to update sublease: ${transactionError.message}`);
      }

      // If RPC function doesn't exist yet, fall back to separate updates
      if (!data && !transactionError) {
        console.log('RPC function not available, using separate updates');

        // Update the sublease record
        const { error: subleaseError } = await supabase
          .from('subleases')
          .update({
            lease_end: formData.endDate || null,
            lease_rent_freq: formData.reviewInterval || null,
            lease_method: formData.leaseMethod || null, // Use the value directly
            lease_type: formData.leaseType || null, // Use the value directly
            lease_dealing: formData.leaseDealing || null,
            lease_furth_term: formData.leaseFurthTerm || null,
            lease_deal_doc: formData.leaseDealDoc || null
          })
          .eq('lease_id', leaseId);

        if (subleaseError) {
          console.error('Error updating subleases:', subleaseError);
          throw new Error(`Failed to update sublease record: ${subleaseError.message}`);
        }

        // Update the land_lots record
        const { error: landLotsError } = await supabase
          .from('land_lots')
          .update({
            lot_perm_use: formData.permittedUse || null
          })
          .eq('lot_id', sublease.lot_id);

        if (landLotsError) {
          console.error('Error updating land_lots:', landLotsError);
          throw new Error(`Failed to update lot information: ${landLotsError.message}`);
        }

        // Update or create the rental_contract record if rental amount is provided
        if (formData.rentalAmount && formData.rentalAmount.trim() !== '') {
          try {
            console.log('Processing rental amount:', formData.rentalAmount);

            // Check if a rental_contract record already exists for this lease
            const { data: existingRental, error: checkRentalError } = await supabase
              .from('rental_contract')
              .select('rental_trans_id')
              .eq('rental_lease', leaseId)
              .maybeSingle();

            if (checkRentalError) {
              console.error('Error checking rental contract:', checkRentalError);
              throw new Error(`Failed to check rental contract: ${checkRentalError.message}`);
            }

            console.log('Existing rental contract check result:', existingRental);

            if (existingRental) {
              // Update existing rental_contract record
              console.log('Updating existing rental contract with ID:', existingRental.rental_trans_id);

              const { data: updateData, error: updateRentalError } = await supabase
                .from('rental_contract')
                .update({
                  rental_amount: parseFloat(formData.rentalAmount),
                  rental_start: sublease.lease_start || null
                })
                .eq('rental_trans_id', existingRental.rental_trans_id)
                .select();

              if (updateRentalError) {
                console.error('Error updating rental contract:', updateRentalError);
                throw new Error(`Failed to update rental contract: ${updateRentalError.message}`);
              }

              console.log('Successfully updated rental contract:', updateData);
            } else {
              // Create new rental_contract record
              console.log('Creating new rental contract for lease ID:', leaseId);

              const { data: insertData, error: insertRentalError } = await supabase
                .from('rental_contract')
                .insert([{
                  // rental_trans_id will be auto-generated by the sequence
                  rental_lease: leaseId,
                  rental_amount: parseFloat(formData.rentalAmount),
                  rental_start: sublease.lease_start || null
                }])
                .select();

              if (insertRentalError) {
                console.error('Error creating rental contract:', insertRentalError);
                throw new Error(`Failed to create rental contract: ${insertRentalError.message}`);
              }

              console.log('Successfully created new rental contract:', insertData);
            }
          } catch (rentalErr) {
            // Log the error but don't fail the whole transaction
            console.error('Error updating/creating rental contract:', rentalErr);
            setError(prev => prev ? `${prev}. Note: Rental amount was not saved properly.` : 'Sublease updated, but rental amount was not saved properly.');
          }
        } else {
          console.log('No rental amount provided, skipping rental contract update');
        }
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating sublease:', err);
      setError(err instanceof Error ? err.message : 'Failed to update sublease');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium">Edit Sublease</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="border-b pb-4 mb-4">
            <h4 className="text-sm font-medium text-gray-500 mb-4">Read-only Information</h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500">
                  Lot ID
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {sublease.lot_id}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Community
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {sublease.community_name || 'N/A'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Leaseholder
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {sublease.holder_name || 'Unassigned'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Start Date
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {sublease.lease_start ? formatDate(sublease.lease_start) : 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Editable Fields</h4>

            {/* Editable fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, endDate: e.target.value }));
                    if (validationErrors.endDate) {
                      setValidationErrors(prev => ({ ...prev, endDate: '' }));
                    }
                  }}
                  className={`mt-1 block w-full rounded-md border ${validationErrors.endDate ? 'border-red-500' : 'border-gray-300'
                    } px-3 py-2 focus:border-primary focus:ring-primary`}
                />
                {validationErrors.endDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.endDate}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">Set the end date for this sublease</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Permitted Use
                </label>
                <input
                  type="text"
                  value={formData.permittedUse.length > 50
                    ? formData.permittedUse.substring(0, 50) + '...'
                    : formData.permittedUse}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, permittedUse: e.target.value }));
                    if (validationErrors.permittedUse) {
                      setValidationErrors(prev => ({ ...prev, permittedUse: '' }));
                    }
                  }}
                  className={`mt-1 block w-full rounded-md border ${validationErrors.permittedUse ? 'border-red-500' : 'border-gray-300'
                    } px-3 py-2 focus:border-primary focus:ring-primary`}
                  placeholder="Enter permitted use"
                  title={formData.permittedUse}
                />
                {validationErrors.permittedUse && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.permittedUse}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.permittedUse.length > 50
                    ? `Showing first 50 of ${formData.permittedUse.length} characters. Hover to see full text.`
                    : 'Specify the allowed use for this lot. URLs will be automatically converted to clickable links when displayed.'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Rental Amount
                </label>
                <input
                  type="number"
                  value={formData.rentalAmount}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, rentalAmount: e.target.value }));
                    if (validationErrors.rentalAmount) {
                      setValidationErrors(prev => ({ ...prev, rentalAmount: '' }));
                    }
                  }}
                  min="0"
                  step="0.01"
                  className={`mt-1 block w-full rounded-md border ${validationErrors.rentalAmount ? 'border-red-500' : 'border-gray-300'
                    } px-3 py-2 focus:border-primary focus:ring-primary`}
                  placeholder="Enter rental amount"
                />
                {validationErrors.rentalAmount && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.rentalAmount}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">The amount charged per rental interval</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Rental Interval
                </label>
                <select
                  value={formData.reviewInterval}
                  onChange={(e) => setFormData(prev => ({ ...prev, reviewInterval: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  <option value="">Select interval</option>
                  {REVIEW_INTERVALS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Choose how often the lease should be reviewed</p>
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Lease Method
                </label>
                <select
                  value={formData.leaseMethod}
                  onChange={(e) => setFormData(prev => ({ ...prev, leaseMethod: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  <option value="">Select method</option>
                  {LEASE_METHODS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Specify the method used for this lease</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Lease Type
                </label>
                <select
                  value={formData.leaseType}
                  onChange={(e) => setFormData(prev => ({ ...prev, leaseType: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  <option value="">Select type</option>
                  {LEASE_TYPES.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Specify the type of this lease</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Lease Dealing
                </label>
                <select
                  value={formData.leaseDealing}
                  onChange={(e) => setFormData(prev => ({ ...prev, leaseDealing: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  <option value="">Select dealing</option>
                  {LEASE_DEALINGS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Specify the dealing for this lease</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Further Term
                </label>
                <input
                  type="text"
                  value={formData.leaseFurthTerm}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, leaseFurthTerm: e.target.value }));
                    if (validationErrors.leaseFurthTerm) {
                      setValidationErrors(prev => ({ ...prev, leaseFurthTerm: '' }));
                    }
                  }}
                  maxLength={30}
                  className={`mt-1 block w-full rounded-md border ${validationErrors.leaseFurthTerm ? 'border-red-500' :
                    formData.leaseFurthTerm.length > 25 ? 'border-amber-300' : 'border-gray-300'
                    } px-3 py-2 focus:border-primary focus:ring-primary`}
                  placeholder="Enter further term (max 30 chars)"
                />
                {validationErrors.leaseFurthTerm && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.leaseFurthTerm}
                  </p>
                )}
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-gray-500">Specify any further term details</p>
                  <p className={`text-xs ${formData.leaseFurthTerm.length > 25 ? 'text-amber-500 font-medium' : 'text-gray-500'
                    }`}>
                    {formData.leaseFurthTerm.length}/30
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Deal Document
                </label>
                <input
                  type="text"
                  value={formData.leaseDealDoc}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, leaseDealDoc: e.target.value }));
                    if (validationErrors.leaseDealDoc) {
                      setValidationErrors(prev => ({ ...prev, leaseDealDoc: '' }));
                    }
                  }}
                  maxLength={1000}
                  className={`mt-1 block w-full rounded-md border ${validationErrors.leaseDealDoc ? 'border-red-500' :
                    formData.leaseDealDoc.length > 900 ? 'border-amber-300' : 'border-gray-300'
                    } px-3 py-2 focus:border-primary focus:ring-primary`}
                  placeholder="Enter deal document reference (max 1000 chars)"
                />
                {validationErrors.leaseDealDoc && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.leaseDealDoc}
                  </p>
                )}
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    Reference to the deal document. URLs will be automatically converted to clickable links when displayed.
                  </p>
                  <p className={`text-xs ${formData.leaseDealDoc.length > 900 ? 'text-amber-500 font-medium' : 'text-gray-500'
                    }`}>
                    {formData.leaseDealDoc.length}/1000
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 sticky bottom-0 pt-4 bg-white">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
